import { useEffect, useMemo, useState } from "react";
import { getApiRootUrl, isOpenUIRuntimeEnabled } from "@/config/runtime";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, PlayCircle } from "lucide-react";
import MiniAppNodeRenderer from "@/components/operate/MiniAppNodeRenderer";
import RecipePreviewCanvas from "@/components/operate/RecipePreviewCanvas";
import OpenUIRenderer from "@/openui/OpenUIRenderer";
import CreateSandboxAgentDialog from "@/components/studio/CreateSandboxAgentDialog";
import StudioMiniAppConfigRenderer, {
  extractMiniAppConfigChildren,
} from "@/components/studio/StudioMiniAppConfigRenderer";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { CopyableRequest } from "@/components/studio/CopyableRequest";
import { WorkflowApiTriggersPanel } from "@/components/studio/WorkflowApiTriggersPanel";
import AgentSpecExecutionObservability from "@/components/studio/AgentSpecExecutionObservability";
import {
  AGENT_RUNTIME_MINIAPPS_QUERY_KEY,
  AGENT_RUNTIME_WORKFLOWS_QUERY_KEY,
} from "@/hooks/invalidateAgentSpecResources";
import { mergeAgentSpecProjectFiles } from "@/components/studio/agentSpecTreeResources";
import { studioThreadsQueryKey } from "@/hooks/useAgentSpecStudioThreads";
import { agentSpecsAPI } from "@/services/api/agentSpecs";
import { miniappsAPI } from "@/services/api/miniapps";
import { resolveMiniAppAction } from "@/services/miniapps/actionBindings";
import {
  recipesAPI,
  type MiniAppDocumentV2,
  type WorkflowDocumentV2,
} from "@/services/api/recipes";
import { buildStructuredSpecFromRecipeDefinitions } from "@/services/transforms/recipeDefinitionToSpec";
import { useContextStore } from "@/store/context";
import {
  type AgentSpecProjectFile,
  type AgentSpecPullResult,
  type AgentSpecStudioMiniAppPreview,
} from "@/types/agentSpec";
import type { MetaRecette } from "@/types/recipe";
import type {
  AppNode,
  AppUIRender,
  MiniAppRuntimePayload,
} from "@/services/api/miniappTypes";

function runtimePayloadToPreview(
  runtime: MiniAppRuntimePayload,
): AgentSpecStudioMiniAppPreview {
  return {
    preview_id: runtime.app_instance_id,
    expires_at: "",
    runtime: {
      app_instance_id: runtime.app_instance_id,
      miniapp_id: runtime.miniapp_id,
      status: String(runtime.status ?? ""),
      state: runtime.state ?? {},
      state_version: runtime.state_version ?? 1,
      ui: runtime.ui
        ? {
            view: runtime.ui.view ?? "main",
            tree: (runtime.ui.tree ?? {}) as Record<string, unknown>,
            format: runtime.ui.format,
            library: runtime.ui.library,
            program: runtime.ui.program,
            initial_state: runtime.ui.initial_state,
            fallback_tree: runtime.ui.fallback_tree as
              | Record<string, unknown>
              | undefined,
          }
        : undefined,
      forms: runtime.forms as Record<string, unknown> | undefined,
      action_bindings: runtime.action_bindings as
        | Record<string, { event_id: string }>
        | undefined,
      effects: runtime.effects as
        | Array<{ type: string; payload?: Record<string, unknown> }>
        | undefined,
    },
  };
}

// isStubOpenUIProgram flags a program Studio emits when the MiniApp
// configuration children were not compiled into a real OpenUI program
// (TextBlock("<miniapp_id>")): the tree is the better preview then.
export function isStubOpenUIProgram(
  program: string | undefined,
  miniAppId: string,
): boolean {
  const text = String(program ?? "").trim();
  if (!text) return true;
  return (
    text.includes(`TextBlock("${miniAppId}")`) ||
    text.includes(`TextBlock('${miniAppId}')`)
  );
}

export type AgentStudioSection =
  "overview" | "workflows" | "miniapps" | "executions" | "plans";
export type AgentStudioResourceState =
  "deployed" | "modified" | "new" | "deleted" | "outsideSpec";

export interface AgentStudioResource {
  id: string;
  kind: "workflow" | "miniapp";
  title: string;
  version: string;
  state: AgentStudioResourceState;
  managementMode: "managed" | "unmanaged";
  ownerAgentSpecId?: string;
  ownerRevision?: number;
  contentHash?: string;
  resourceVersion?: number;
  draftDocument?: WorkflowDocumentV2 | MiniAppDocumentV2;
  runtimeDocument?: WorkflowDocumentV2 | MiniAppDocumentV2;
}

interface AgentStudioRuntimeResource<T> {
  document: T;
  managementMode: "managed" | "unmanaged";
  ownerAgentSpecId?: string;
  ownerRevision?: number;
  contentHash?: string;
  resourceVersion?: number;
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, child]) => [key, stableValue(child)]),
  );
}

function sameDocument(left: unknown, right: unknown) {
  return (
    JSON.stringify(stableValue(left)) === JSON.stringify(stableValue(right))
  );
}

export function isSandboxGovernanceProfile(
  profile?: string | null,
): boolean {
  return String(profile ?? "")
    .trim()
    .toLowerCase() === "sandbox";
}

export function isStudioPreviewExecutionBusy(
  state: string | null | undefined,
): boolean {
  switch (String(state ?? "").trim().toLowerCase()) {
    case "running":
    case "pending":
    case "draft":
      return true;
    default:
      return false;
  }
}

export function studioWorkflowPreviewBusy(input: {
  startPending: boolean;
  executionState?: string | null;
  statusLoadFailed?: boolean;
}): boolean {
  if (input.startPending) return true;
  if (input.statusLoadFailed) return false;
  return isStudioPreviewExecutionBusy(input.executionState);
}

/** Draft preview is allowed on sandbox, development, and integration_test Agents. */
export function studioPreviewExecutionPollMs(
  state: string | null | undefined,
): number | false {
  return isStudioPreviewExecutionBusy(state) ? 2_000 : false;
}

export function studioPreviewExecutionBadgeVariant(
  state: string | null | undefined,
): "success" | "destructive" | "secondary" {
  switch (String(state ?? "").trim().toLowerCase()) {
    case "completed":
    case "complete":
    case "succeeded":
      return "success";
    case "failed":
    case "errored":
    case "cancelled":
      return "destructive";
    default:
      return "secondary";
  }
}

export function isDraftPreviewAllowedProfile(
  profile?: string | null,
): boolean {
  switch (String(profile ?? "").trim().toLowerCase()) {
    case "sandbox":
    case "development":
    case "dev":
    case "integration_test":
    case "integration-test":
      return true;
    default:
      return false;
  }
}

export function isStudioPreviewSandboxForbiddenError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    /agent_spec_forbidden/i.test(message) ||
    /draft preview is only available/i.test(message) ||
    /sandbox Agents/i.test(message)
  );
}

export function formatStudioPreviewError(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  governanceProfile?: string | null,
): string {
  const profile =
    String(governanceProfile ?? "").trim() ||
    t("agentSpec.previewUnknownProfile");
  if (isStudioPreviewSandboxForbiddenError(error)) {
    return t("agentSpec.previewRequiresSandboxError", { profile });
  }
  const raw = error instanceof Error ? error.message : String(error ?? "");
  const jsonMatch = raw.match(/API Error \d+:\s*(\{[\s\S]*\})\s*$/);
  if (jsonMatch) {
    try {
      const body = JSON.parse(jsonMatch[1]) as { message?: unknown };
      if (typeof body.message === "string" && body.message.trim()) {
        return body.message.trim();
      }
    } catch {
      // Keep the original message when the body is not JSON.
    }
  }
  return raw;
}

function parseDraftDocuments(files: AgentSpecProjectFile[]) {
  const workflows = new Map<string, WorkflowDocumentV2>();
  const miniapps = new Map<string, MiniAppDocumentV2>();
  for (const file of files) {
    try {
      const document = JSON.parse(file.content) as Record<string, unknown>;
      if (
        file.path.includes("/workflows/") &&
        document.$schema === "agentmaurice.workflow/v2" &&
        typeof document.workflow_id === "string"
      ) {
        workflows.set(
          document.workflow_id,
          document as unknown as WorkflowDocumentV2,
        );
      }
      if (
        file.path.includes("/miniapps/") &&
        document.$schema === "agentmaurice.miniapp/v2" &&
        typeof document.miniapp_id === "string"
      ) {
        miniapps.set(
          document.miniapp_id,
          document as unknown as MiniAppDocumentV2,
        );
      }
    } catch {
      // Draft diagnostics surface invalid JSON; resource navigation stays usable.
    }
  }
  return { workflows, miniapps };
}

export function mergeAgentStudioResources<
  T extends WorkflowDocumentV2 | MiniAppDocumentV2,
>(
  kind: "workflow" | "miniapp",
  draftItems: Map<string, T>,
  runtimeItems: Map<string, AgentStudioRuntimeResource<T>>,
): AgentStudioResource[] {
  const ids = new Set([...draftItems.keys(), ...runtimeItems.keys()]);
  return [...ids]
    .map((id) => {
      const draftDocument = draftItems.get(id);
      const runtime = runtimeItems.get(id);
      const document = draftDocument ?? runtime?.document;
      const state: AgentStudioResourceState = !runtime
        ? "new"
        : !draftDocument
          ? runtime.managementMode === "unmanaged"
            ? "outsideSpec"
            : "deleted"
          : sameDocument(draftDocument, runtime.document)
            ? "deployed"
            : "modified";
      return {
        id,
        kind,
        title: document?.title ?? id,
        version: document?.version ?? "—",
        state,
        managementMode: runtime?.managementMode ?? "managed",
        ownerAgentSpecId: runtime?.ownerAgentSpecId,
        ownerRevision: runtime?.ownerRevision,
        contentHash: runtime?.contentHash,
        resourceVersion: runtime?.resourceVersion,
        draftDocument,
        runtimeDocument: runtime?.document,
      };
    })
    .sort((left, right) => left.title.localeCompare(right.title));
}

export function useAgentSpecResourceModel(
  organizationId: string | null,
  agentId: string | null,
  threadId: string | null,
  pull: AgentSpecPullResult | null,
) {
  const threadQuery = useQuery({
    queryKey: ["agent-spec-studio-thread", organizationId, agentId, threadId],
    queryFn: () =>
      agentSpecsAPI.getStudioThread(organizationId!, agentId!, threadId!),
    enabled: Boolean(organizationId && agentId && threadId),
    retry: false,
  });
  const hostThreadsQuery = useQuery({
    queryKey: studioThreadsQueryKey(organizationId, agentId),
    queryFn: () => agentSpecsAPI.listStudioThreads(organizationId!, agentId!),
    enabled: Boolean(organizationId && agentId),
    retry: false,
    staleTime: 60_000,
  });
  const hostThreads = hostThreadsQuery.data?.threads ?? [];
  const hostDraftQueries = useQueries({
    queries: hostThreads.map((thread) => ({
      queryKey: [
        "agent-spec-studio-thread",
        organizationId,
        agentId,
        thread.id,
      ],
      queryFn: () =>
        agentSpecsAPI.getStudioThread(organizationId!, agentId!, thread.id),
      enabled: Boolean(organizationId && agentId && thread.id),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const workflowsQuery = useQuery({
    queryKey: [AGENT_RUNTIME_WORKFLOWS_QUERY_KEY, organizationId, agentId],
    queryFn: () => recipesAPI.listRecipes(organizationId!, agentId!),
    enabled: Boolean(organizationId && agentId),
    retry: false,
  });
  const miniappsQuery = useQuery({
    queryKey: [AGENT_RUNTIME_MINIAPPS_QUERY_KEY, organizationId, agentId],
    queryFn: () => recipesAPI.listMiniApps(organizationId!, agentId!),
    enabled: Boolean(organizationId && agentId),
    retry: false,
  });

  const model = useMemo(() => {
    const draftFiles = mergeAgentSpecProjectFiles(
      pull?.files,
      threadQuery.data?.revision?.files,
      ...hostDraftQueries.map((query) => query.data?.revision?.files),
    );
    const draft = parseDraftDocuments(draftFiles);
    const runtimeWorkflows = new Map<
      string,
      AgentStudioRuntimeResource<WorkflowDocumentV2>
    >();
    for (const item of workflowsQuery.data ?? []) {
      const document = item.metadata?.workflow as
        WorkflowDocumentV2 | undefined;
      if (document) {
        runtimeWorkflows.set(item.id, {
          document,
          managementMode:
            item.metadata?.managementMode === "unmanaged"
              ? "unmanaged"
              : "managed",
          ownerAgentSpecId: item.metadata?.ownerAgentSpecId,
          ownerRevision: item.metadata?.ownerRevision,
          contentHash: item.metadata?.contentHash,
          resourceVersion: item.metadata?.resourceVersion,
        });
      }
    }
    const runtimeMiniApps = new Map(
      (miniappsQuery.data ?? []).map((item) => [
        item.miniapp.miniapp_id,
        {
          document: item.miniapp,
          managementMode: item.management_mode,
          ownerAgentSpecId: item.owner_agent_spec_id,
          ownerRevision: item.owner_revision,
          contentHash: item.content_hash,
          resourceVersion: item.resource_version,
        },
      ]),
    );

    return {
      workflows: mergeAgentStudioResources(
        "workflow",
        draft.workflows,
        runtimeWorkflows,
      ),
      miniapps: mergeAgentStudioResources(
        "miniapp",
        draft.miniapps,
        runtimeMiniApps,
      ),
      revision: threadQuery.data?.revision,
      loading: workflowsQuery.isLoading || miniappsQuery.isLoading,
      runtimeError: workflowsQuery.error ?? miniappsQuery.error ?? null,
      retryRuntime: async () => {
        await Promise.all([
          workflowsQuery.refetch(),
          miniappsQuery.refetch(),
        ]);
      },
    };
  }, [
    miniappsQuery.data,
    miniappsQuery.error,
    miniappsQuery.refetch,
    hostDraftQueries,
    pull?.files,
    threadQuery.data,
    workflowsQuery.data,
    workflowsQuery.error,
    workflowsQuery.refetch,
  ]);

  return model;
}

function stateVariant(state: AgentStudioResourceState) {
  if (state === "deployed") return "success";
  if (state === "modified") return "warning";
  if (state === "new") return "default";
  if (state === "outsideSpec") return "outline";
  return "destructive";
}

function workflowRecipe(document: WorkflowDocumentV2): MetaRecette {
  return {
    id: document.workflow_id,
    name: document.title,
    version: document.version,
    description: document.description,
    structured_spec: buildStructuredSpecFromRecipeDefinitions([
      { ...document, id: document.workflow_id, name: document.title },
    ]),
    is_active: true,
    createdAt: new Date(0),
    updatedAt: new Date(0),
  };
}

function resolveLocalSchemaReference(
  root: Record<string, unknown>,
  reference: string,
): unknown {
  if (!reference.startsWith("#/")) return undefined;
  return reference
    .slice(2)
    .split("/")
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))
    .reduce<unknown>((value, part) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) {
        return undefined;
      }
      return (value as Record<string, unknown>)[part];
    }, root);
}

function stringSchemaExample(schema: Record<string, unknown>, key?: string) {
  switch (schema.format) {
    case "date":
      return "2026-01-01";
    case "date-time":
      return "2026-01-01T00:00:00Z";
    case "email":
      return "user@example.com";
    case "hostname":
      return "example.com";
    case "ipv4":
      return "192.0.2.1";
    case "uri":
    case "uri-reference":
      return "https://example.com/resource";
    case "uuid":
      return "00000000-0000-4000-8000-000000000000";
    default:
      return key || "string";
  }
}

function schemaExample(
  schema: unknown,
  root: Record<string, unknown>,
  activeReferences: Set<string>,
  key?: string,
): unknown {
  if (schema === true) return {};
  if (!schema || typeof schema !== "object" || Array.isArray(schema)) return {};
  const record = schema as Record<string, unknown>;
  if (record.example !== undefined) return structuredClone(record.example);
  if (Array.isArray(record.examples) && record.examples.length > 0) {
    return structuredClone(record.examples[0]);
  }
  if (record.default !== undefined) return structuredClone(record.default);
  if (record.const !== undefined) return structuredClone(record.const);
  if (Array.isArray(record.enum) && record.enum.length > 0) {
    return structuredClone(record.enum[0]);
  }

  if (typeof record.$ref === "string") {
    if (activeReferences.has(record.$ref)) return {};
    const referenced = resolveLocalSchemaReference(root, record.$ref);
    if (referenced !== undefined) {
      const nextReferences = new Set(activeReferences).add(record.$ref);
      return schemaExample(referenced, root, nextReferences, key);
    }
  }

  for (const alternatives of [record.oneOf, record.anyOf]) {
    if (Array.isArray(alternatives)) {
      const alternative = alternatives.find((item) => item !== null && item !== false);
      if (alternative !== undefined) {
        return schemaExample(alternative, root, activeReferences, key);
      }
    }
  }

  const types = Array.isArray(record.type) ? record.type : [record.type];
  const type = types.find((candidate) => candidate && candidate !== "null");
  const properties = record.properties as Record<string, unknown> | undefined;
  if (type === "object" || properties) {
    return Object.fromEntries(
      Object.entries(properties ?? {}).map(([propertyKey, value]) => [
        propertyKey,
        schemaExample(value, root, activeReferences, propertyKey),
      ]),
    );
  }
  if (type === "array") {
    const minimum =
      typeof record.minItems === "number" ? Math.max(0, record.minItems) : 1;
    const count =
      typeof record.maxItems === "number"
        ? Math.min(minimum || 1, Math.max(0, record.maxItems))
        : minimum || 1;
    return Array.from({ length: count }, () =>
      schemaExample(record.items, root, activeReferences, key),
    );
  }
  if (type === "boolean") return false;
  if (type === "integer" || type === "number") {
    if (typeof record.minimum === "number") return record.minimum;
    if (typeof record.exclusiveMinimum === "number") {
      return type === "integer"
        ? Math.floor(record.exclusiveMinimum) + 1
        : record.exclusiveMinimum + 0.1;
    }
    return 0;
  }
  if (type === "string") return stringSchemaExample(record, key);
  return {};
}

export function buildJsonSchemaExample(schema: unknown): unknown {
  if (!schema || typeof schema !== "object" || Array.isArray(schema)) return {};
  return schemaExample(schema, schema as Record<string, unknown>, new Set());
}

function runtimeURL(
  publicURL: string,
  agentId: string,
  resourcePath: string,
): string {
  return `${publicURL.replace(/\/+$/, "")}/api/v2/agents/${encodeURIComponent(agentId)}/${resourcePath}`;
}

export function buildWorkflowRuntimeCurl(
  publicURL: string,
  agentId: string,
  workflow: WorkflowDocumentV2,
): string {
  return [
    `curl -sS -X POST "${runtimeURL(publicURL, agentId, `workflows/${encodeURIComponent(workflow.workflow_id)}/executions`)}" \\`,
    '  -H "X-API-Key: $AGENTMAURICE_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify({ inputs: buildJsonSchemaExample(workflow.input_schema) })}'`,
  ].join("\n");
}

export function buildWorkflowAsyncRuntimeCurl(
  publicURL: string,
  agentId: string,
  workflow: WorkflowDocumentV2,
): string {
  return [
    `curl -sS -X POST "${runtimeURL(publicURL, agentId, `workflows/${encodeURIComponent(workflow.workflow_id)}/executions/async`)}" \\`,
    '  -H "X-API-Key: $AGENTMAURICE_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify({ inputs: buildJsonSchemaExample(workflow.input_schema) })}'`,
  ].join("\n");
}

export function buildWorkflowStatusRuntimeCurl(
  publicURL: string,
  agentId: string,
  workflow: WorkflowDocumentV2,
): string {
  return [
    `curl -sS "${runtimeURL(publicURL, agentId, `workflows/${encodeURIComponent(workflow.workflow_id)}/executions/<EXECUTION_ID>`)}" \\`,
    '  -H "X-API-Key: $AGENTMAURICE_API_KEY"',
  ].join("\n");
}

export function buildMiniAppSessionRuntimeCurl(
  publicURL: string,
  agentId: string,
  miniapp: MiniAppDocumentV2,
): string {
  return [
    `curl -sS -X POST "${runtimeURL(publicURL, agentId, `miniapps/${encodeURIComponent(miniapp.miniapp_id)}/sessions`)}" \\`,
    '  -H "X-API-Key: $AGENTMAURICE_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify({ seed_state: miniapp.initial_state ?? {}, seed_data: {} })}'`,
  ].join("\n");
}

export function buildMiniAppEventRuntimeCurl(
  publicURL: string,
  agentId: string,
  miniapp: MiniAppDocumentV2,
  eventId: string,
): string {
  return [
    `curl -sS -X POST "${runtimeURL(publicURL, agentId, `miniapps/${encodeURIComponent(miniapp.miniapp_id)}/sessions/<INSTANCE_ID>/events/${encodeURIComponent(eventId)}`)}" \\`,
    '  -H "X-API-Key: $AGENTMAURICE_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    '  -d \'{"payload":{},"expected_state_version":1}\'',
  ].join("\n");
}

function StructuredValue({
  title,
  value,
}: {
  title: string;
  value: unknown;
}) {
  return (
    <section className="space-y-2">
      <h3 className="font-semibold">{title}</h3>
      <pre className="max-h-80 overflow-auto rounded-xl bg-muted p-4 font-mono text-xs text-foreground">
        {JSON.stringify(value ?? {}, null, 2)}
      </pre>
    </section>
  );
}

export function AgentSpecResourceExplorer({
  resource,
  organizationId,
  agentId,
  threadId,
  revisionId,
  governanceProfile,
  publicURL = getApiRootUrl(),
}: {
  resource: AgentStudioResource | null;
  organizationId: string;
  agentId: string;
  threadId?: string | null;
  revisionId?: string | null;
  governanceProfile?: string | null;
  publicURL?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deployments = useContextStore((state) => state.deployments);
  const selectedDeployment = deployments.find((item) => item.id === agentId);
  // Prefer server governance, but fall back to the Agent's deployment_kind —
  // a draft may not have a check or plan yet, even on sandbox/development.
  const resolvedGovernanceProfile =
    String(governanceProfile ?? "").trim() ||
    String(selectedDeployment?.deploymentKind ?? "").trim() ||
    null;
  const [eventId, setEventId] = useState("");
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewConfirmation, setPreviewConfirmation] = useState<
    "workflow" | "miniapp" | null
  >(null);
  const [createSandboxOpen, setCreateSandboxOpen] = useState(false);
  const [sandboxSuggestedName, setSandboxSuggestedName] = useState("");
  const draftPreviewAllowed = isDraftPreviewAllowedProfile(
    resolvedGovernanceProfile,
  );
  const canPreviewDraft =
    Boolean(threadId && revisionId) &&
    (resource?.state === "new" || resource?.state === "modified");
  const [miniAppPreviewResult, setMiniAppPreviewResult] =
    useState<AgentSpecStudioMiniAppPreview | null>(null);
  const [miniAppPreviewUrl, setMiniAppPreviewUrl] = useState(
    "https://example.com",
  );
  const [openUIParseError, setOpenUIParseError] = useState<string | null>(
    null,
  );
  const openUIEnabled = isOpenUIRuntimeEnabled();
  const previewProgram = miniAppPreviewResult?.runtime.ui?.program;
  useEffect(() => {
    setOpenUIParseError(null);
  }, [previewProgram]);
  const mapPreviewError = (error: unknown) =>
    setPreviewError(
      formatStudioPreviewError(error, t, resolvedGovernanceProfile),
    );
  const buildMiniAppSeed = (miniapp: MiniAppDocumentV2) => ({
    ...(miniapp.initial_state ?? {}),
    url: miniAppPreviewUrl.trim() || "",
    brief:
      (miniapp.initial_state as { brief?: unknown } | undefined)?.brief ?? {},
  });
  const startMiniAppSession = async (miniapp: MiniAppDocumentV2) => {
    const seed = buildMiniAppSeed(miniapp);
    if (canPreviewDraft && threadId && revisionId) {
      return agentSpecsAPI.startStudioMiniAppPreview(
        organizationId,
        agentId,
        threadId,
        revisionId,
        miniapp.miniapp_id,
        seed,
      );
    }
    const runtime = await miniappsAPI.createInstance(agentId, miniapp.miniapp_id, {
      miniAppVersion: miniapp.version,
      seedState: seed,
    });
    return runtimePayloadToPreview(runtime);
  };
  const workflowPreview = useMutation({
    mutationFn: (workflow: WorkflowDocumentV2) =>
      agentSpecsAPI.previewStudioWorkflow(
        organizationId,
        agentId,
        threadId!,
        revisionId!,
        workflow.workflow_id,
        buildJsonSchemaExample(workflow.input_schema) as Record<string, unknown>,
      ),
    onError: mapPreviewError,
    onSuccess: (result) => {
      setPreviewError(null);
      void queryClient.invalidateQueries({
        queryKey: ["agent-workflow-executions", organizationId, agentId],
      });
      return result;
    },
  });
  const previewExecutionId = workflowPreview.data?.execution_id ?? null;
  const previewExecutionQuery = useQuery({
    queryKey: [
      "agent-workflow-execution",
      organizationId,
      agentId,
      previewExecutionId,
    ],
    queryFn: () =>
      recipesAPI.getScopedRecipeExecutionStatus(
        organizationId,
        agentId,
        previewExecutionId!,
        { include_logs: true },
      ),
    enabled: Boolean(previewExecutionId),
    refetchInterval: (query) =>
      studioPreviewExecutionPollMs(
        query.state.data?.state ?? workflowPreview.data?.state,
      ),
  });
  const previewExecutionState =
    previewExecutionQuery.data?.state ?? workflowPreview.data?.state;
  const previewBusy = studioWorkflowPreviewBusy({
    startPending: workflowPreview.isPending,
    executionState: previewExecutionState,
    statusLoadFailed: previewExecutionQuery.isError,
  });
  const miniAppPreview = useMutation({
    mutationFn: (miniapp: MiniAppDocumentV2) => startMiniAppSession(miniapp),
    onError: mapPreviewError,
    onSuccess: (result) => {
      setPreviewError(null);
      setMiniAppPreviewResult(result);
      const seededUrl = result.runtime.state?.url;
      if (typeof seededUrl === "string" && seededUrl.trim()) {
        setMiniAppPreviewUrl(seededUrl);
      }
    },
  });
  const miniAppEvent = useMutation({
    mutationFn: async ({
      miniapp,
      selectedEvent,
      payload,
    }: {
      miniapp: MiniAppDocumentV2;
      selectedEvent: string;
      payload?: Record<string, unknown>;
    }) => {
      const url = miniAppPreviewUrl.trim();
      let session = miniAppPreviewResult;
      const currentUrl =
        typeof session?.runtime.state?.url === "string"
          ? session.runtime.state.url
          : "";
      // Re-seed when the URL field changed so {{ state.url }} resolves.
      if (!session || currentUrl !== url) {
        session = await startMiniAppSession(miniapp);
        setMiniAppPreviewResult(session);
      }
      if (canPreviewDraft && threadId && revisionId) {
        return agentSpecsAPI.sendStudioMiniAppPreviewEvent(
          organizationId,
          agentId,
          threadId,
          revisionId,
          miniapp.miniapp_id,
          session.preview_id,
          selectedEvent,
          {
            payload: payload ?? {},
            form_data: { url, ...(payload ?? {}) },
            expected_state_version: session.runtime.state_version,
          },
        );
      }
      const runtime = await miniappsAPI.sendEvent(
        session.runtime.app_instance_id,
        selectedEvent,
        {
          payload: payload ?? {},
          form_data: { url, ...(payload ?? {}) },
          expected_state_version: session.runtime.state_version,
        },
      );
      return runtimePayloadToPreview(runtime);
    },
    onSuccess: (result) => {
      setPreviewError(null);
      setMiniAppPreviewResult(result);
    },
    onError: mapPreviewError,
  });
  useEffect(() => {
    setMiniAppPreviewResult(null);
    setPreviewError(null);
  }, [resource?.id]);
  if (!resource) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          {t("agentSpec.selectResource")}
        </CardContent>
      </Card>
    );
  }
  const document = resource.draftDocument ?? resource.runtimeDocument;
  if (!document) return null;
  const isWorkflow = resource.kind === "workflow";
  const workflow = isWorkflow ? (document as WorkflowDocumentV2) : null;
  const miniapp = !isWorkflow ? (document as MiniAppDocumentV2) : null;
  const canShowPreviewTab = Boolean(miniapp) || canPreviewDraft;
  const usesRuntimeMiniAppPreview = Boolean(miniapp) && !canPreviewDraft;
  const miniAppConfigChildren = extractMiniAppConfigChildren(
    miniAppPreviewResult?.runtime.ui?.tree,
  );
  // Same renderer as Run (MiniAppPanel): the OpenUI program when the server
  // compiled one, the tree only as a fallback.
  const previewUI = miniAppPreviewResult?.runtime.ui as
    | AppUIRender
    | undefined;
  const shouldRenderPreviewOpenUI =
    openUIEnabled &&
    previewUI?.format === "openui_lang" &&
    typeof previewUI.program === "string" &&
    previewUI.program.trim().length > 0 &&
    !isStubOpenUIProgram(previewUI.program, miniapp?.miniapp_id ?? "") &&
    !openUIParseError;
  const previewFallbackTree =
    (previewUI?.fallback_tree as AppNode | undefined) ??
    (previewUI?.tree as AppNode | undefined);
  const sendPreviewAction = (
    actionId: string,
    payload?: Record<string, unknown>,
  ) => {
    if (!miniapp) return;
    // Same resolution as Run (useMiniAppRuntime): bound event_id and
    // payload_template of the instance action_bindings.
    const action = resolveMiniAppAction(
      miniAppPreviewResult?.runtime.action_bindings,
      actionId,
      payload,
    );
    miniAppEvent.mutate({
      miniapp,
      selectedEvent: action.eventId,
      payload: action.payload,
    });
  };
  const effectiveEvent =
    eventId || miniapp?.events?.[0]?.event_id || "EVENT_ID";
  const workflowCurl = workflow
    ? buildWorkflowRuntimeCurl(publicURL, agentId, workflow)
    : "";
  const workflowAsyncCurl = workflow
    ? buildWorkflowAsyncRuntimeCurl(publicURL, agentId, workflow)
    : "";
  const workflowStatusCurl = workflow
    ? buildWorkflowStatusRuntimeCurl(publicURL, agentId, workflow)
    : "";
  const miniAppCreateCurl = miniapp
    ? buildMiniAppSessionRuntimeCurl(publicURL, agentId, miniapp)
    : "";
  const miniAppEventCurl = miniapp
    ? buildMiniAppEventRuntimeCurl(
        publicURL,
        agentId,
        miniapp,
        effectiveEvent,
      )
    : "";

  const openCreateSandboxDialog = () => {
    const suggested = selectedDeployment?.name?.trim()
      ? `${selectedDeployment.name.trim()} Sandbox`
      : "";
    setSandboxSuggestedName(suggested);
    setCreateSandboxOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs text-muted-foreground">
              {isWorkflow
                ? t("agentSpec.workflowLabel")
                : t("agentSpec.miniappLabel")}
            </p>
            <CardTitle>{resource.title}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Badge variant={stateVariant(resource.state)}>
              {t(`agentSpec.resourceState.${resource.state}`)}
            </Badge>
            <Badge variant="outline">v{resource.version}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="summary">
          <TabsList>
            <TabsTrigger value="summary">
              {t("agentSpec.resourceSummary")}
            </TabsTrigger>
            {isWorkflow ? (
              <TabsTrigger value="graph">
                {t("agentSpec.resourceGraph")}
              </TabsTrigger>
            ) : null}
            {isWorkflow ? (
              // Executions of this Workflow (lot R4): the former Agent Spec
              // tree node, now a tab of each Workflow.
              <TabsTrigger value="executions" data-testid="resource-executions-tab">
                {t("agentSpec.executionsNavigation")}
              </TabsTrigger>
            ) : null}
            <TabsTrigger value="api" data-testid="resource-api-tab">
              {isWorkflow
                ? t("workflowApi.tabLabel")
                : t("agentSpec.resourceApiCall")}
            </TabsTrigger>
            {canShowPreviewTab ? (
              <TabsTrigger value="preview" data-testid="resource-preview-tab">
                {t("agentSpec.resourcePreview")}
              </TabsTrigger>
            ) : null}
          </TabsList>
          <TabsContent value="summary" className="pt-5 space-y-5">
            <p className="text-sm text-muted-foreground">
              {document.description || t("agentSpec.noResourceDescription")}
            </p>
            <section className="space-y-3">
              <h3 className="font-semibold">
                {t("agentSpec.resourceOwnershipAndProvenance")}
              </h3>
              <dl className="grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                <div>
                  <dt className="text-muted-foreground">
                    {t("agentSpec.managementMode")}
                  </dt>
                  <dd>{resource.managementMode}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("agentSpec.ownerRevision")}
                  </dt>
                  <dd>{resource.ownerRevision ?? "—"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("agentSpec.resourceVersion")}
                  </dt>
                  <dd>{resource.resourceVersion ?? "—"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("agentSpec.ownerAgentSpec")}
                  </dt>
                  <dd className="break-all font-mono text-xs">
                    {resource.ownerAgentSpecId ?? "—"}
                  </dd>
                </div>
              </dl>
              {resource.contentHash ? (
                <div className="text-sm">
                  <p className="text-muted-foreground">
                    {t("agentSpec.contentHash")}
                  </p>
                  <code className="break-all text-xs">
                    {resource.contentHash}
                  </code>
                </div>
              ) : null}
            </section>
            {workflow ? (
              <>
                <div className="grid gap-5 xl:grid-cols-2">
                  <StructuredValue
                    title={t("agentSpec.inputSchema")}
                    value={workflow.input_schema}
                  />
                  <StructuredValue
                    title={t("agentSpec.outputSchema")}
                    value={workflow.output_schema}
                  />
                </div>
                <StructuredValue
                  title={t("agentSpec.workflowForms")}
                  value={workflow.forms ?? []}
                />
                <h3 className="font-semibold">
                  {t("agentSpec.workflowActions")}
                </h3>
                <ul className="space-y-2">
                  {workflow.actions.map((action, index) => (
                    <li
                      key={String(action.id ?? index)}
                      className="rounded-lg border border-border p-3 font-mono text-xs"
                    >
                      <p>
                        {String(action.id ?? index)} ·{" "}
                        {String(action.type ?? "action")}
                      </p>
                      <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-muted p-3 text-foreground">
                        {JSON.stringify(action, null, 2)}
                      </pre>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {miniapp ? (
              <>
                <dl className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">
                      {t("agentSpec.miniappRenderer")}
                    </dt>
                    <dd>{miniapp.ui.renderer}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">
                      {t("agentSpec.miniappComponent")}
                    </dt>
                    <dd>{miniapp.ui.component}</dd>
                  </div>
                </dl>
                <div className="grid gap-5 xl:grid-cols-2">
                  <StructuredValue
                    title={t("agentSpec.stateSchema")}
                    value={miniapp.state_schema}
                  />
                  <StructuredValue
                    title={t("agentSpec.initialState")}
                    value={miniapp.initial_state ?? {}}
                  />
                </div>
                <h3 className="font-semibold">
                  {t("agentSpec.eventMappings")}
                </h3>
                <ul className="space-y-2">
                  {miniapp.events.map((event) => (
                    <li
                      key={event.event_id}
                      className="rounded-lg border border-border p-3"
                    >
                      <code className="text-xs">{event.event_id}</code>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {event.trigger} →{" "}
                        {event.workflow.workflow_id || "capability"}
                      </p>
                      <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-muted p-3 font-mono text-xs text-foreground">
                        {JSON.stringify(event, null, 2)}
                      </pre>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </TabsContent>
          {workflow ? (
            <TabsContent value="graph" className="pt-5">
              <RecipePreviewCanvas
                recipe={workflowRecipe(workflow)}
                inspectable
                className="h-[clamp(32rem,68dvh,60rem)]"
              />
            </TabsContent>
          ) : null}
          {isWorkflow ? (
            <TabsContent value="executions" className="pt-5">
              <AgentSpecExecutionObservability
                organizationId={organizationId}
                agentId={agentId}
                workflowId={resource.id}
              />
            </TabsContent>
          ) : null}
          <TabsContent value="api" className="pt-5 space-y-4">
            {workflow ? (
              <WorkflowApiTriggersPanel
                organizationId={organizationId}
                agentId={agentId}
                workflow={workflow}
                inputExample={buildJsonSchemaExample(workflow.input_schema)}
                agentSyncCurl={workflowCurl}
                agentAsyncCurl={workflowAsyncCurl}
                agentStatusCurl={workflowStatusCurl}
                notYetApplied={resource.state === "new"}
                publicURL={publicURL}
              />
            ) : null}
            {miniapp ? (
              <a
                href={`/studio/api-keys?${new URLSearchParams({
                  organizationId,
                  agentId,
                  deploymentId: agentId,
                }).toString()}`}
                className="text-sm font-medium text-accent-foreground underline-offset-4 hover:underline"
              >
                {t("agentSpec.manageApiKeys")}
              </a>
            ) : null}
            {miniapp && resource.state === "new" ? (
              <p className="rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-warning">
                {t("agentSpec.apiAvailableAfterApply")}
              </p>
            ) : null}
            {miniapp ? (
              <>
                <h3 className="font-semibold">
                  {t("agentSpec.createMiniappSession")}
                </h3>
                <CopyableRequest value={miniAppCreateCurl} />
                {miniapp.events.length ? (
                  <label className="block text-sm font-medium">
                    {t("agentSpec.miniappEventLabel")}
                    <select
                      value={effectiveEvent}
                      onChange={(event) => setEventId(event.target.value)}
                      className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2"
                    >
                      {miniapp.events.map((event) => (
                        <option key={event.event_id} value={event.event_id}>
                          {event.event_id}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <CopyableRequest value={miniAppEventCurl} />
              </>
            ) : null}
          </TabsContent>
          {canShowPreviewTab ? (
            <TabsContent value="preview" className="space-y-4 pt-5">
              <p className="text-sm text-muted-foreground">
                {usesRuntimeMiniAppPreview
                  ? t("agentSpec.previewRuntimeDescription")
                  : t("agentSpec.previewSandboxDescription")}
              </p>
              {!usesRuntimeMiniAppPreview && !draftPreviewAllowed ? (
                <div
                  role="status"
                  className="space-y-3 rounded-lg border border-warning/30 bg-warning-soft p-4"
                  data-testid="preview-sandbox-required"
                >
                  <p className="font-medium text-foreground">
                    {t("agentSpec.previewRequiresSandboxTitle")}
                  </p>
                  <p className="text-sm text-foreground-soft">
                    {t("agentSpec.previewRequiresSandboxBody", {
                      profile:
                        String(resolvedGovernanceProfile ?? "").trim() ||
                        t("agentSpec.previewUnknownProfile"),
                    })}
                  </p>
                  <Button
                    type="button"
                    onClick={openCreateSandboxDialog}
                    data-testid="preview-create-sandbox-agent"
                  >
                    {t("agentSpec.previewCreateSandboxAgent")}
                  </Button>
                  <p className="text-xs text-foreground-soft">
                    {t("agentSpec.previewSwitchAgentHint")}
                  </p>
                </div>
              ) : null}
              {previewError ? (
                <p role="alert" className="text-sm text-destructive">
                  {previewError}
                </p>
              ) : null}
              {canPreviewDraft && draftPreviewAllowed && workflow ? (
                <>
                  <Button
                    type="button"
                    onClick={() => setPreviewConfirmation("workflow")}
                    disabled={previewBusy}
                    data-testid="preview-run-workflow"
                  >
                    {previewBusy ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                    ) : (
                      <PlayCircle className="mr-2 h-4 w-4" />
                    )}
                    {previewBusy
                      ? t("agentSpec.runWorkflowPreviewBusy")
                      : t("agentSpec.runWorkflowPreview")}
                  </Button>
                  {workflowPreview.data ? (
                    <div
                      className="space-y-3 rounded-lg border border-success/30 bg-success-soft p-4 text-sm"
                      data-testid="preview-workflow-execution"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">
                          {t("agentSpec.previewExecutionStarted")}
                        </p>
                        <Badge
                          variant={studioPreviewExecutionBadgeVariant(
                            previewExecutionQuery.data?.state ??
                              workflowPreview.data.state,
                          )}
                        >
                          {previewExecutionQuery.data?.state ??
                            workflowPreview.data.state}
                        </Badge>
                        {previewExecutionQuery.isFetching &&
                        studioPreviewExecutionPollMs(
                          previewExecutionQuery.data?.state ??
                            workflowPreview.data.state,
                        ) ? (
                          <Loader2 className="h-4 w-4 animate-spin text-accent-foreground motion-reduce:animate-none" />
                        ) : null}
                      </div>
                      <code className="block break-all text-xs">
                        {workflowPreview.data.execution_id}
                      </code>
                      {previewExecutionQuery.data?.error ? (
                        <p
                          role="alert"
                          className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-destructive"
                        >
                          {previewExecutionQuery.data.error}
                        </p>
                      ) : null}
                      {previewExecutionQuery.data ? (
                        <>
                          <div>
                            <p className="mb-1 font-medium">
                              {t("agentSpec.executionLogs")}
                            </p>
                            <pre className="max-h-56 overflow-auto rounded-xl bg-muted p-3 font-mono text-xs text-foreground">
                              {(previewExecutionQuery.data.logs ?? [])
                                .map(
                                  (log) =>
                                    `${log.level ?? "info"} ${log.component ?? "runtime"} — ${log.message ?? ""}`,
                                )
                                .join("\n") || t("agentSpec.executionLogsEmpty")}
                            </pre>
                          </div>
                          <div>
                            <p className="mb-1 font-medium">
                              {t("agentSpec.executionResult")}
                            </p>
                            <pre className="max-h-56 overflow-auto rounded-xl bg-muted p-3 font-mono text-xs text-foreground">
                              {JSON.stringify(
                                previewExecutionQuery.data.result ??
                                  previewExecutionQuery.data.data ??
                                  {},
                                null,
                                2,
                              )}
                            </pre>
                          </div>
                        </>
                      ) : previewExecutionQuery.isError ? (
                        <p role="alert" className="text-destructive">
                          {t("agentSpec.executionLoadError")}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : null}
              {miniapp &&
              (usesRuntimeMiniAppPreview || draftPreviewAllowed) ? (
                <>
                  <label className="block space-y-1.5 text-sm">
                    <span className="font-medium text-foreground">URL</span>
                    <input
                      type="url"
                      value={miniAppPreviewUrl}
                      onChange={(event) =>
                        setMiniAppPreviewUrl(event.target.value)
                      }
                      placeholder="https://example.com"
                      data-testid="preview-miniapp-url"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground"
                    />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={() =>
                        usesRuntimeMiniAppPreview
                          ? miniAppPreview.mutate(miniapp)
                          : setPreviewConfirmation("miniapp")
                      }
                      disabled={miniAppPreview.isPending}
                      data-testid="preview-run-miniapp"
                    >
                      {miniAppPreview.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                      ) : (
                        <PlayCircle className="mr-2 h-4 w-4" />
                      )}
                      {t("agentSpec.startMiniappPreview")}
                    </Button>
                    {miniAppPreviewResult && miniapp.events?.[0]?.event_id ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={
                          miniAppEvent.isPending || !miniAppPreviewUrl.trim()
                        }
                        data-testid="preview-submit-miniapp-event"
                        onClick={() =>
                          miniAppEvent.mutate({
                            miniapp,
                            selectedEvent: miniapp.events[0].event_id,
                            payload: { url: miniAppPreviewUrl.trim() },
                          })
                        }
                      >
                        {miniAppEvent.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                        ) : null}
                        {miniapp.events[0].event_id}
                      </Button>
                    ) : null}
                  </div>
                  {miniAppConfigChildren.length > 0 ? (
                    <StudioMiniAppConfigRenderer
                      nodes={miniAppConfigChildren}
                      state={miniAppPreviewResult?.runtime.state ?? {}}
                      urlValue={miniAppPreviewUrl}
                      onUrlChange={setMiniAppPreviewUrl}
                      disabled={miniAppEvent.isPending}
                      onEvent={(selectedEvent, payload) =>
                        miniAppEvent.mutate({
                          miniapp,
                          selectedEvent,
                          payload,
                        })
                      }
                    />
                  ) : null}
                  {!miniAppConfigChildren.length && shouldRenderPreviewOpenUI ? (
                    <Card
                      className="border-info/30 bg-card"
                      data-testid="preview-miniapp-openui"
                    >
                      <CardContent className="space-y-4 pt-6">
                        <OpenUIRenderer
                          ui={previewUI}
                          onEvent={sendPreviewAction}
                          onOpenForm={() => undefined}
                          onParseError={setOpenUIParseError}
                        />
                      </CardContent>
                    </Card>
                  ) : null}
                  {!miniAppConfigChildren.length &&
                  !shouldRenderPreviewOpenUI &&
                  previewFallbackTree ? (
                    <Card
                      className="border-info/30 bg-card"
                      data-testid="preview-miniapp-tree"
                    >
                      <CardContent className="space-y-4 pt-6">
                        <MiniAppNodeRenderer
                          node={previewFallbackTree}
                          onOpenForm={() => undefined}
                          onEvent={sendPreviewAction}
                        />
                      </CardContent>
                    </Card>
                  ) : null}
                  {miniAppPreviewResult ? (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        {t("agentSpec.previewStateVersion", {
                          version:
                            miniAppPreviewResult.runtime.state_version,
                        })}
                      </p>
                      <pre className="max-h-64 overflow-auto rounded-xl bg-muted p-4 font-mono text-xs text-foreground">
                        {JSON.stringify(
                          miniAppPreviewResult.runtime.state,
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  ) : null}
                </>
              ) : null}
            </TabsContent>
          ) : null}
        </Tabs>
      </CardContent>
      <ConfirmDialog
        open={previewConfirmation !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewConfirmation(null);
        }}
        title={t("agentSpec.previewConfirmTitle")}
        description={t("agentSpec.previewConfirmDescription")}
        confirmText={t("agentSpec.previewConfirmAction")}
        cancelText={t("common.cancel")}
        disabled={
          previewBusy || workflowPreview.isPending || miniAppPreview.isPending
        }
        onConfirm={() => {
          if (previewConfirmation === "workflow" && workflow) {
            workflowPreview.mutate(workflow);
          }
          if (previewConfirmation === "miniapp" && miniapp) {
            miniAppPreview.mutate(miniapp);
          }
          setPreviewConfirmation(null);
        }}
      />
      {createSandboxOpen ? (
        <CreateSandboxAgentDialog
          open={createSandboxOpen}
          onOpenChange={setCreateSandboxOpen}
          organizationId={organizationId}
          suggestedName={sandboxSuggestedName}
          sourceAgentId={agentId}
          sourceThreadId={threadId ?? ""}
          sourceRevisionId={revisionId ?? ""}
          continuePreviewResourceId={
            isWorkflow && workflow ? workflow.workflow_id : ""
          }
        />
      ) : null}
    </Card>
  );
}
